/**
 * PM bonuses — TRBUD-459. The wire shapes for who earned each PM's bonus, the
 * one shared bonus rate, and the Reports panel that adds them up.
 *
 * ## Eden's rulings (27 October 2026)
 *
 * - ONE credited mechanic per PM, and ONE shared adjustable rate. No individual
 *   rates, splits or category matrix.
 * - Eligible work orders carry the Preventive category, matched by its fixed
 *   code. Not the `is_preventive` flag, a work-order type or a title.
 * - A PM earns ONE bonus, ever, and only while it is complete. Eden, 30 October
 *   2026: "1 completed PM = 1 bonus, if he has to go back and fix it again and
 *   succeeds then he gets to keep the 1 bonus, but he fails to fix the PM upon
 *   redo no 1 bonus." A reopened PM counts for nobody while it waits on its
 *   redo, and counts again, once, on its credit's original day ("Original
 *   month") when it is completed again.
 * - The dollar amount is not known, so none is hard-coded. With no rate the
 *   report shows counts and says the rate is not set.
 *
 * ## The database decides, these shapes report it
 *
 * Eligibility is one view (`v_pm_bonus_work_order`) and the writers are two
 * routines (`set_work_order_pm_credit`, `set_pm_bonus_rate`). The report is ONE
 * server read that returns one row per eligible work order and the totals made
 * from those same rows, so a mechanic's count always equals the work orders
 * listed under it.
 *
 * ## A bonus is not maintenance cost and not a wage record
 *
 * Kept apart from labour rates (a valuation for make-versus-buy) and from every
 * cost total. It is an estimate of a bonus, not proof one was paid; there is no
 * paid or unpaid state.
 */
import { z } from "zod";
import { calendarDate, strictObject } from "./requests.js";
import { isRealCalendarDay } from "./field-shapes.js";

/** Why a work order does not earn a PM bonus, or `eligible`. */
export const pmCreditEligibilities = [
  "eligible",
  "not_pm",
  "draft",
  "itemized_ticket",
  "cancelled",
  "imported",
  "not_completed",
  "reopened",
] as const;
export type PmCreditEligibility = (typeof pmCreditEligibilities)[number];

/** The sentence a person reads for each answer. One place, so the card and the dialog agree. */
export const pmCreditEligibilityNotes: Record<PmCreditEligibility, string> = {
  eligible: "This completed PM can earn a bonus once a mechanic and the day are recorded.",
  not_pm: "Only a work order with the Preventive category earns a PM bonus.",
  draft: "A draft work order does not earn a PM bonus.",
  itemized_ticket: "An itemized ticket does not earn a PM bonus. Credit the PM package inside it.",
  cancelled: "A cancelled work order does not earn a PM bonus.",
  imported: "A work order imported from MaintainX does not earn a PM bonus.",
  not_completed: "Record who did the PM once the work order is complete.",
  reopened: "This PM is reopened and waiting on its redo. Its one bonus counts again, on the day already recorded, once it is complete again.",
};

/** Someone a PM can be credited to. */
export interface PmCreditPerson {
  userId: string;
  displayName: string;
  /** Shown beside the name when two people share one. */
  email: string;
  active: boolean;
  /** Holds the mechanic role now. Mechanics are listed first. */
  isMechanic: boolean;
}

/** The credit as recorded. */
export interface PmCreditRecord {
  mechanicUserId: string;
  mechanicName: string;
  mechanicEmail: string;
  mechanicActive: boolean;
  /** The fleet calendar day the PM was done, YYYY-MM-DD. */
  performedOn: string;
  recordedByName: string | null;
  recordedAt: string;
  /** Send this back as `expectedVersion` to correct the credit. */
  version: number;
  /** Why it was last corrected; null on a first record. */
  correctionReason: string | null;
}

/**
 * What the work records point to. A SUGGESTION, shown as one, never saved by
 * itself. A mechanic is suggested only when exactly one person has work marks
 * or time entries on the job; with several or none, the person chooses.
 */
export interface PmCreditSuggestion {
  mechanicUserId: string | null;
  /** The last day the work records show work, else today. */
  performedOn: string;
  dateBasis: "work_records" | "today";
  /** Everyone with work marks or live time entries on the job, latest first. */
  candidates: Array<{ userId: string; displayName: string; lastWorkedOn: string }>;
}

/** `GET /work-orders/:id/pm-credit`. */
export interface PmCreditState {
  workOrderId: string;
  eligibility: PmCreditEligibility;
  credit: PmCreditRecord | null;
  suggestion: PmCreditSuggestion;
  /** Who the picker offers. Empty when the caller cannot record. */
  people: PmCreditPerson[];
  /**
   * `any`: record or correct for anyone (management). `own`: record yourself
   * when nobody is credited yet. Null: read only.
   */
  canRecord: "any" | "own" | null;
  /** The fleet's today, the latest day the picker allows. */
  today: string;
}

/**
 * `PUT /work-orders/:id/pm-credit`.
 *
 * `expectedVersion` is the version the caller is correcting, and is required
 * for a correction; a first record sends null. `reason` is required for a
 * correction and ignored on a first record. Sending the credit already
 * recorded changes nothing, so a retry is safe.
 */
export const setPmCreditBody = strictObject({
  mechanicUserId: z.uuid(),
  performedOn: calendarDate,
  expectedVersion: z.number().int().min(1).max(1_000_000).nullish(),
  reason: z.string().trim().max(500).nullish(),
});
export type SetPmCreditBody = z.output<typeof setPmCreditBody>;

/** What a credit write answers. */
export interface SetPmCreditReceipt {
  version: number;
  /** False when the credit asked for was already recorded. */
  changed: boolean;
}

/* ── the shared rate ─────────────────────────────────────────────── */

/** The calendar day after a YYYY-MM-DD day. Calendar arithmetic, so no clock change can move it. */
export function nextCalendarDay(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

/**
 * A PM bonus in dollars and cents, as a string. Positive, at most two places,
 * fitting `numeric(10,2)`. Never a JS number.
 */
export const pmBonusAmountString = z
  .string()
  .trim()
  .regex(/^\d{1,8}(\.\d{1,2})?$/, "expected an amount in dollars and cents, such as 75 or 75.50")
  .refine((value) => Number.parseFloat(value) > 0, "a PM bonus must be more than zero");

/**
 * `PUT /pm-bonus/rate`. The day is always explicit: the screen offers today
 * for the first rate and tomorrow for a change, so a change cannot reprice a
 * PM already credited by accident.
 */
export const setPmBonusRateBody = strictObject({
  amount: pmBonusAmountString,
  effectiveFrom: calendarDate,
});
export type SetPmBonusRateBody = z.output<typeof setPmBonusRateBody>;

export interface SetPmBonusRateReceipt {
  rateId: string;
  /** False when that amount was already in force from that day. */
  changed: boolean;
}

/** One live rate period. `to` is the first day it no longer covers, or null for open. */
export interface PmBonusRatePeriod {
  id: string;
  amount: string;
  currency: "USD";
  from: string;
  to: string | null;
  setByName: string | null;
  setAt: string;
}

/** `GET /pm-bonus/rates`. */
export interface PmBonusRatesResponse {
  /** Live periods, earliest first. Empty until the first rate is set. */
  periods: PmBonusRatePeriod[];
  /** The period covering today, if any. */
  current: PmBonusRatePeriod | null;
  canEdit: boolean;
  today: string;
  /**
   * The earliest day a change can start without altering a PM already
   * credited. The screen offers it (or tomorrow) as the default.
   */
  earliestChangeDay: string;
}

/* ── the report ──────────────────────────────────────────────────── */

const reportDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(isRealCalendarDay);

/** `GET /reports/pm-bonuses`. Its own scope: the Reports category and vendor filters do not apply. */
export const pmBonusReportQuery = z
  .object({
    timeframe: z.enum(["all", "30d", "quarter", "ytd"]).default("all"),
    start: reportDay.optional(),
    end: reportDay.optional(),
    mechanicUserId: z.uuid().optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.start && !value.end) {
      context.addIssue({ code: "custom", message: "Provide an end date with the start date." });
    } else if (value.start && value.end && value.start > value.end) {
      context.addIssue({ code: "custom", message: "The start date must be on or before the end date." });
    }
  });
export type PmBonusReportQuery = z.output<typeof pmBonusReportQuery>;

/** One eligible PM work order. The rows the counts are made of. */
export interface PmBonusWorkOrderRow {
  workOrderId: string;
  number: string;
  title: string;
  unitLabel: string | null;
  /** The day the PM was done; null when it still needs a credit. */
  performedOn: string | null;
  /** The fleet day it first reached a completed stage. Used only to place a PM still needing a credit. */
  completedOn: string | null;
  mechanicUserId: string | null;
  mechanicName: string | null;
  /** The shared rate covering `performedOn`; null when no rate covers that day. */
  rate: string | null;
  /** Equal to `rate`: one PM earns one rate. Null when unpriced or not yet credited. */
  bonus: string | null;
  /** For a correction. Null when not yet credited. */
  creditVersion: number | null;
  /** How many times it was reopened for rework, from the status events. */
  reopenCount: number;
  /** The fleet day of each reopen, oldest first. */
  reopenedOn: string[];
}

/** PMs priced at one rate, so a period that crosses a rate change shows each rate. */
export interface PmBonusRateGroup {
  rate: string;
  count: number;
  subtotal: string;
}

export interface PmBonusMechanicRow {
  userId: string;
  displayName: string;
  email: string;
  active: boolean;
  /** Credited PMs in the period. Equal to the rows listed under this mechanic. */
  credited: number;
  priced: number;
  /** Credited on a day no rate covers. */
  unpriced: number;
  /** Sum of the priced PMs' rates. Equal to the sum of `rateGroups` subtotals. */
  bonusTotal: string;
  rateGroups: PmBonusRateGroup[];
}

export interface PmBonusTotals {
  /** Distinct credited PMs. Equal to the sum of the mechanics' counts. */
  credited: number;
  priced: number;
  unpriced: number;
  bonusTotal: string;
  rateGroups: PmBonusRateGroup[];
}

export interface PmBonusReport {
  /** Null for Lifetime. */
  start: string | null;
  end: string;
  /** "This quarter · 1 Oct to 30 Oct 2026". */
  label: string;
  /** The mechanic filter echoed; null for everyone. */
  mechanicUserId: string | null;
  /** False until the first rate is set; the panel then shows counts only. */
  rateConfigured: boolean;
  currency: "USD";
  mechanics: PmBonusMechanicRow[];
  /** The whole selection. With a mechanic filter, that mechanic's figures. */
  totals: PmBonusTotals;
  /** Every credited PM in the period (and filter), the rows behind the counts. */
  workOrders: PmBonusWorkOrderRow[];
  /**
   * Completed PMs with no credit yet, placed in the period by the day they were
   * completed. Never counted or priced. Not narrowed by the mechanic filter,
   * because they have no mechanic.
   */
  needsReview: PmBonusWorkOrderRow[];
  /**
   * PMs reopened and not complete again yet. They count for nobody until the
   * redo is complete, so a past period's total drops while one waits here and
   * comes back when it is done. A credited one is placed by its credit's day
   * (and narrowed by the mechanic filter), an uncredited one by the day it was
   * first completed. `bonus` is always null.
   */
  waitingOnRedo: PmBonusWorkOrderRow[];
  /** For the mechanic filter: everyone credited in the period, plus active mechanics. */
  mechanicOptions: Array<{ userId: string; displayName: string; email: string; active: boolean }>;
}
